import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, ClipboardList, ShieldCheck, ArrowLeft, FileDown, Image as ImageIcon, RotateCcw, Save, Printer,
  Pencil, StickyNote, ListChecks,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { api, fileUrl } from '../../lib/api';
import { toast } from 'sonner';

const SectionCard = ({ sec, progressSec, onTick, onNotes, onEdit, editing, onEditField }) => {
  const pct = progressSec ? progressSec.percent : 0;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`runbook-section-${sec.key}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="font-serif font-bold text-[#1a2744] text-sm">{sec.title}</p>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant="outline" className={`text-[10px] font-bold ${pct === 100 ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-slate-50 text-slate-600 border-slate-300'}`} data-testid={`runbook-progress-${sec.key}`}>
            {progressSec ? `${progressSec.checked}/${progressSec.total}` : '0/0'}
          </Badge>
          <Button size="sm" variant="ghost" onClick={onEdit} className="h-7 px-2 text-[11px] font-bold" data-testid={`runbook-edit-toggle-${sec.key}`}>
            <Pencil className="h-3 w-3 mr-1" /> {editing ? 'Done' : 'Edit steps'}
          </Button>
        </div>
      </div>

      {editing ? (
        <div>
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Section title</label>
          <Input value={sec.title} onChange={(e) => onEditField('title', e.target.value)} className="h-10 text-sm font-bold mt-1 mb-3" data-testid={`runbook-title-${sec.key}`} />
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Checklist items (one per line)</label>
          <Textarea value={(sec.items || []).join('\n')} onChange={(e) => onEditField('items', e.target.value)} rows={Math.max(3, (sec.items || []).length)} className="text-sm mt-1" data-testid={`runbook-items-${sec.key}`} />
        </div>
      ) : (
        <div className="space-y-1">
          {(sec.items || []).map((item, idx) => {
            const st = progressSec?.items?.[idx] || {};
            return (
              <label key={idx} className={`flex items-start gap-3 rounded-lg border p-2.5 cursor-pointer transition-colors ${st.checked ? 'bg-emerald-50/60 border-emerald-200' : 'bg-white border-slate-200 hover:bg-slate-50'}`} data-testid={`runbook-step-${sec.key}-${idx}`}>
                <Checkbox checked={!!st.checked} onCheckedChange={(v) => onTick(idx, !!v)} className="mt-0.5" data-testid={`runbook-check-${sec.key}-${idx}`} />
                <span className="min-w-0">
                  <span className={`text-sm font-semibold ${st.checked ? 'text-emerald-900' : 'text-slate-700'}`}>{item}</span>
                  {st.checked && st.checked_at && (
                    <span className="block text-[10px] text-emerald-700/80 mt-0.5">{String(st.checked_at).replace('T', ' ').slice(0, 16)} UTC{st.checked_by ? ` · ${st.checked_by}` : ''}</span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      )}

      <div className="mt-3">
        <label className="text-[11px] font-bold text-slate-500 flex items-center gap-1"><StickyNote className="h-3 w-3" /> Section notes</label>
        <Input defaultValue={progressSec?.notes || ''} onBlur={(e) => { if (e.target.value !== (progressSec?.notes || '')) onNotes(e.target.value); }} placeholder="e.g. Waiting on Steve's OK for…" className="h-10 text-sm mt-1" data-testid={`runbook-notes-${sec.key}`} />
      </div>
    </div>
  );
};

export default function AdminLaunchRunbook() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [sections, setSections] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [editingKey, setEditingKey] = useState('');
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    api.get('/admin/launch-runbook').then((r) => {
      setData(r.data);
      setSections(r.data.sections || []);
      setDirty(false);
      setError(false);
      if (r.data.last_pdf) setResult({ pdf: r.data.last_pdf, png: r.data.last_png });
    }).catch(() => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateSection = (idx, field, value) => {
    setSections((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      if (field === 'items') return { ...s, items: value.split('\n') };
      return { ...s, [field]: value };
    }));
    setDirty(true);
  };

  const save = async () => {
    setBusy('save');
    try {
      const payload = sections.map((s) => ({ ...s, items: s.items.map((i) => i.trim()).filter(Boolean) }));
      await api.put('/admin/launch-runbook', { sections: payload });
      toast.success('Runbook saved');
      setEditingKey('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy('');
  };

  const reset = async () => {
    setBusy('reset');
    try {
      await api.post('/admin/launch-runbook/reset');
      toast.success('Restored default runbook');
      setEditingKey('');
      load();
    } catch { toast.error('Reset failed'); }
    setBusy('');
  };

  const resetProgress = async () => {
    setBusy('reset-progress');
    try {
      await api.post('/admin/launch-runbook/progress/reset');
      toast.success('Progress reset — nothing was published or posted');
      load();
    } catch { toast.error('Progress reset failed'); }
    setBusy('');
  };

  const generate = async () => {
    setBusy('generate');
    try {
      const r = await api.post('/admin/launch-runbook/generate');
      setResult(r.data);
      toast.success('One-page runbook generated');
      load();
    } catch { toast.error('Generation failed'); }
    setBusy('');
  };

  const tick = async (sectionKey, index, checked) => {
    try {
      await api.put('/admin/launch-runbook/progress/item', { section: sectionKey, index, checked });
      load();
    } catch { toast.error('Progress update failed'); }
  };

  const saveNotes = async (sectionKey, notes) => {
    try {
      await api.put('/admin/launch-runbook/progress/section-notes', { section: sectionKey, notes });
      toast.success('Section notes saved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Notes save failed'); }
  };

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-center justify-between gap-3" data-testid="runbook-error">
        <p className="text-sm font-semibold text-red-800">Couldn't load the Launch Day Runbook.</p>
        <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold">Retry</Button>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const prog = data.progress || { percent: 0, checked: 0, total: 0, sections: [] };
  const progByKey = Object.fromEntries((prog.sections || []).map((s) => [s.key, s]));

  return (
    <div data-testid="admin-launch-runbook">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-1">
        <div>
          <h1 className="font-serif font-black text-2xl text-[#1a2744] flex items-center gap-2"><ClipboardList className="h-6 w-6 text-[#C9A84C]" /> Launch Day Runbook</h1>
          <p className="text-sm text-slate-500 mt-1">The exact order of what to approve, publish, post, and verify — tick steps as you go, printable as one page.</p>
        </div>
        <Button asChild variant="outline" className="h-11 font-bold btn-press shrink-0" data-testid="runbook-back-launch-btn">
          <Link to="/admin/launch-content"><ArrowLeft className="h-4 w-4 mr-1.5" /> Launch Content</Link>
        </Button>
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start mt-3 mb-4" data-testid="runbook-privacy-note">
        <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">{data.note}</p>
      </div>

      {/* Overall progress (Phase 62) */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4" data-testid="runbook-overall-progress">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-1.5"><ListChecks className="h-4 w-4 text-[#C9A84C]" /> Launch day progress</p>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`text-[10px] font-bold ${prog.percent === 100 ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-amber-50 text-amber-900 border-amber-300'}`} data-testid="runbook-overall-percent">
              {prog.percent}% · {prog.checked}/{prog.total} steps
            </Badge>
            <Button size="sm" variant="outline" onClick={resetProgress} disabled={busy === 'reset-progress'} className="h-8 text-[11px] font-bold btn-press" data-testid="runbook-reset-progress-btn">
              <RotateCcw className="h-3 w-3 mr-1" /> Reset Progress
            </Button>
          </div>
        </div>
        <Progress value={prog.percent} className="h-2.5" />
        {prog.last_checked_at && (
          <p className="text-[11px] text-slate-500 mt-2" data-testid="runbook-last-checked">Last checked {String(prog.last_checked_at).replace('T', ' ').slice(0, 16)} UTC{prog.last_checked_by ? ` by ${prog.last_checked_by}` : ''}</p>
        )}
        <p className="text-[11px] text-slate-400 mt-1">Checking a step records progress only — it never publishes, posts, emails, or activates anything.</p>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 mb-5">
        <Button onClick={generate} disabled={busy === 'generate'} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="runbook-generate-btn">
          {busy === 'generate' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Printer className="h-4 w-4 mr-2 text-[#C9A84C]" />} Generate Printable Runbook
        </Button>
        {dirty && (
          <Button onClick={save} disabled={busy === 'save'} variant="outline" className="h-11 font-bold btn-press border-[#C9A84C] text-[#7A5C12]" data-testid="runbook-save-btn">
            {busy === 'save' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Changes
          </Button>
        )}
        <Button onClick={reset} disabled={busy === 'reset'} variant="outline" className="h-11 font-bold btn-press" data-testid="runbook-reset-btn">
          <RotateCcw className="h-4 w-4 mr-2" /> Restore Default
        </Button>
      </div>

      {result?.pdf && (
        <div className="rounded-xl border border-emerald-300 bg-emerald-50/60 p-4 mb-5" data-testid="runbook-result">
          <p className="text-sm font-bold text-emerald-900 mb-2">Printable runbook ready</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => window.open(fileUrl(result.pdf), '_blank', 'noopener')} className="h-9 text-xs font-bold btn-press bg-white" data-testid="runbook-pdf-btn">
              <FileDown className="h-3.5 w-3.5 mr-1" /> Download PDF
            </Button>
            {result.png && (
              <Button size="sm" variant="outline" onClick={() => window.open(fileUrl(result.png), '_blank', 'noopener')} className="h-9 text-xs font-bold btn-press bg-white" data-testid="runbook-png-btn">
                <ImageIcon className="h-3.5 w-3.5 mr-1" /> PNG Preview
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Interactive sections */}
      <div className="space-y-4" data-testid="runbook-sections">
        {sections.map((sec, idx) => (
          <SectionCard
            key={sec.key || idx}
            sec={sec}
            progressSec={progByKey[sec.key]}
            editing={editingKey === sec.key}
            onEdit={() => setEditingKey((k) => (k === sec.key ? '' : sec.key))}
            onEditField={(field, value) => updateSection(idx, field, value)}
            onTick={(itemIdx, checked) => tick(sec.key, itemIdx, checked)}
            onNotes={(notes) => saveNotes(sec.key, notes)}
          />
        ))}
      </div>
      {data.updated_at && <p className="text-[11px] text-slate-400 mt-3">Last edited {String(data.updated_at).slice(0, 10)}</p>}
    </div>
  );
}
